import axios from "axios";
import { getPortalSlug } from "@/lib/portal";
import { safeLocalStorage } from "@/lib/safeStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

const TOKEN_KEY = "simpler_access";
const REFRESH_KEY = "simpler_refresh";

export function getToken() {
  return safeLocalStorage.getItem(TOKEN_KEY);
}
export function getRefreshToken() {
  return safeLocalStorage.getItem(REFRESH_KEY);
}
export function setTokens(access, refresh) {
  if (access) safeLocalStorage.setItem(TOKEN_KEY, access);
  if (refresh) safeLocalStorage.setItem(REFRESH_KEY, refresh);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("simpler:auth-changed", { detail: { type: "set" } }));
  }
}
export function clearTokens() {
  safeLocalStorage.removeItem(TOKEN_KEY);
  safeLocalStorage.removeItem(REFRESH_KEY);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("simpler:auth-changed", { detail: { type: "clear" } }));
  }
}

export const api = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  const portalSlug = getPortalSlug();

  config.headers = config.headers || {};

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (portalSlug) {
    config.headers["X-Portal-Slug"] = portalSlug;
  }

  return config;
});

// Simple refresh flow — one retry on 401
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && getRefreshToken()) {
      original._retry = true;
      try {
        if (!refreshing) {
          const portalSlug = getPortalSlug();

          refreshing = axios
            .post(
              `${API_URL}/auth/refresh`,
              { refresh_token: getRefreshToken() },
              {
                headers: portalSlug
                  ? { "X-Portal-Slug": portalSlug }
                  : {},
              }
            )
            .then((r) => {
              setTokens(r.data.access_token, r.data.refresh_token);
              return r.data.access_token;
            })
            .finally(() => { refreshing = null; });
        }
        const newToken = await refreshing;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        clearTokens();
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

// ---- PP24: cache curto das chamadas que toda tela repete + registro de uso sem atrapalhar ----
const PP24_TTL = 60000;
const PP24_URLS = [/\/rbac\/me\/effective$/, /\/alerts\/summary$/, /^\/organization$/];
const pp24Mem = new Map();
const pp24Voo = new Map();
const pp24Inicio = Date.now();
const pp24Path = (c) =>
  String(c.url || "").replace(/^https?:\/\/[^/]+/, "").replace(/^.*\/api(?=\/)/, "").split("?")[0];
const pp24Key = (c) => `${(getToken() || "").slice(-24)}|${pp24Path(c)}|${JSON.stringify(c.params || {})}`;
function pp24Ler(k) {
  let e = pp24Mem.get(k);
  if (!e) {
    try { e = JSON.parse(sessionStorage.getItem("pp24:" + k) || "null"); } catch (_) { e = null; }
  }
  return e && Date.now() - e.t < PP24_TTL ? e : null;
}
function pp24Gravar(k, data) {
  const e = { t: Date.now(), data };
  pp24Mem.set(k, e);
  try { sessionStorage.setItem("pp24:" + k, JSON.stringify(e)); } catch (_) {}
}
function pp24Limpar() {
  pp24Mem.clear();
  try {
    const ks = [];
    for (let i = 0; i < sessionStorage.length; i++) ks.push(sessionStorage.key(i));
    ks.filter((x) => x && x.startsWith("pp24:")).forEach((x) => sessionStorage.removeItem(x));
  } catch (_) {}
}
const pp24Resp = (data, config) => ({ data, status: 200, statusText: "OK", headers: {}, config, request: {} });

api.interceptors.request.use(async (config) => {
  const metodo = (config.method || "get").toLowerCase();
  const path = pp24Path(config);
  if (metodo !== "get") {
    if (/\/activity\/ping$/.test(path)) {
      const falta = 4000 - (Date.now() - pp24Inicio);
      if (falta > 0) await new Promise((r) => setTimeout(r, falta));
    } else {
      pp24Limpar();
    }
    return config;
  }
  if (config._pp24 || !PP24_URLS.some((re) => re.test(path))) return config;
  const k = pp24Key(config);
  const e = pp24Ler(k);
  if (e) {
    config.adapter = () => Promise.resolve(pp24Resp(e.data, config));
    return config;
  }
  if (pp24Voo.has(k)) {
    const p = pp24Voo.get(k).promise;
    config.adapter = () => p.then((data) => pp24Resp(data, config));
    return config;
  }
  let ok, falha;
  const promise = new Promise((a, b) => { ok = a; falha = b; });
  promise.catch(() => {});
  pp24Voo.set(k, { promise, ok, falha });
  config._pp24 = k;
  return config;
});

api.interceptors.response.use(
  (r) => {
    const k = r && r.config && r.config._pp24;
    if (k) {
      if (r.status === 200) pp24Gravar(k, r.data);
      const v = pp24Voo.get(k);
      if (v) { v.ok(r.data); pp24Voo.delete(k); }
    }
    return r;
  },
  (error) => {
    const k = error && error.config && error.config._pp24;
    const v = k && pp24Voo.get(k);
    if (v) { v.falha(error); pp24Voo.delete(k); }
    return Promise.reject(error);
  }
);
